import type { SelfContainedCommand, CommandExecutionContext } from 'types/commands';
import type { StandardToolResult } from 'types/standardToolResults';
import { createSuccessResult, createErrorResult } from 'types/standardToolResults';
import { createFileLogger } from '../../../../utils/logger';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';

const log = createFileLogger('MARKDOWNDOC_CREATE');

export const markdownDocCreateCommand: SelfContainedCommand = {
  id: 'markdowndoc_create',
  description: '📄 CREATE: Create a new markdown document.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Display name for the markdown document',
        required: false,
        defaultValue: 'New Markdown',
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space to create the document in',
        required: false,
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder to place the document in',
        required: false,
      },
      {
        name: 'createWindow',
        type: 'boolean',
        description: 'Whether to open the editor after creation. Default true.',
        required: false,
        defaultValue: true,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { name: 'Project Notes' },
        expectedOutput: createSuccessResult('Markdown document created', { entityId: 'md-123' }),
        description: 'Create a new markdown document',
      },
    ],
    bad: [],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      name = 'New Markdown',
      spaceId: providedSpaceId,
      folderId,
      createWindow = true,
    } = params;

    const spaceId = providedSpaceId || context.spaceId;

    try {
      log.log('[markdowndoc_create] Creating markdown document:', { name, spaceId });

      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const entity = await entityStore.actions.createEntity({
        entityType: 'MarkdownDoc',
        name: name.trim(),
        spaceId,
        skeleton: {
          content: '',
          groupIds: folderId ? [folderId] : [],
        },
      });

      const entityId = entity._id || entity.id;

      if (entityId && spaceId) {
        try {
          const parentId = folderId || spaceId;
          const { default: useDataspaceTreeStore } = await import('../../../../stores/dataspaceTreeStore');
          const treeStore = useDataspaceTreeStore.getState();
          treeStore._applyAddNode(`${parentId}:${entityId}`, {
            id: entityId,
            parentId,
            pos: 'M',
            expanded: false,
            kind: 'canonical',
          });
        } catch (treeErr) {
          log.warn('[markdowndoc_create] Tree registration failed (non-fatal):', treeErr);
        }
      }

      if (createWindow !== false && entityId) {
        try {
          const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();
          await executeCommand('space_open_window', { entityId });
        } catch (windowErr) {
          log.warn('[markdowndoc_create] Failed to open window (non-fatal):', windowErr);
        }
      }

      log.log('[markdowndoc_create] Markdown document created:', entityId);

      return createSuccessResult(`Markdown document created: "${name.trim()}"`, {
        entityId,
        name: name.trim(),
      });
    } catch (err: any) {
      log.error('[markdowndoc_create] Failed:', err);
      return createErrorResult('Failed to create markdown document', err.message || 'Unknown error', 'execution');
    }
  },
};

entityCreationRegistry.register({
  commandId: 'markdowndoc_create',
  entityType: 'MarkdownDoc',
  displayName: 'Markdown',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'FileText',
    phosphorWeight: 'duotone',
  },
  description: 'Create a markdown document',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'system',
  sortOrder: 22,
});
